/**
 * Conferência da foto antes de ela entrar no checklist: ver, refazer ou usar.
 *
 * A foto já chega comprimida à tela (é a MESMA que vai subir), então o que o
 * operador aprova é exatamente o que o gestor vai ver.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { compressPhoto } from "./photo-compress";
import { IconCamera } from "../../components/icons";

export function PhotoReview({ file, itemLabel, onUse, onCancel }: {
  file: Blob;
  itemLabel: string;
  onUse: (blob: Blob) => void;
  onCancel: () => void;
}) {
  const [original, setOriginal] = useState<Blob>(file);
  const [pronta, setPronta] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const refazer = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let vivo = true;
    setPronta(null);
    compressPhoto(original).then((b) => { if (vivo) setPronta(b); });
    return () => { vivo = false; };
  }, [original]);

  useEffect(() => {
    if (!pronta) return;
    const u = URL.createObjectURL(pronta);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [pronta]);

  return createPortal(
    <div className="fixed inset-0 z-50 bg-gray-900 flex flex-col" role="dialog" aria-modal="true" aria-label="Conferir foto">
      <div className="px-4 py-3 text-white">
        <p className="text-xs uppercase tracking-wide text-white/60">Conferir foto</p>
        <p className="text-sm font-medium truncate">{itemLabel}</p>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center px-3">
        {url && pronta ? (
          <img src={url} alt="Foto para conferir" className="max-w-full max-h-full object-contain rounded-lg" />
        ) : (
          <p className="text-sm text-white/70">Preparando a foto…</p>
        )}
      </div>
      <div className="p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] space-y-2">
        {pronta && <p className="text-center text-xs text-white/60">{Math.max(1, Math.round(pronta.size / 1024))} KB · confira se o problema está visível e nítido</p>}
        <input
          ref={refazer}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) setOriginal(f);
            e.target.value = "";
          }}
        />
        <div className="grid grid-cols-3 gap-2">
          <button className="btn-secondary min-h-[3rem]" onClick={onCancel}>Cancelar</button>
          <button className="btn-secondary min-h-[3rem]" onClick={() => refazer.current?.click()}>
            <IconCamera className="w-4 h-4" /> Refazer
          </button>
          <button className="btn-primary min-h-[3rem]" disabled={!pronta} onClick={() => pronta && onUse(pronta)}>
            Usar foto
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
